import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { runReport } from '../../api';
import { useReports } from './ReportsContext';
import DataGrid, { applyPinnedTo } from '../common/DataGrid';
import DateRangePicker from '../common/DateRangePicker';
import { currencyFormatter } from '../valueformatter';
import './Reports.css';

// Headers that Title Casing would mangle.
const HEADER_OVERRIDES = {
    gross_roi: 'Gross ROI',
    ip_roi: 'IP ROI',
    tp_roi: 'TP ROI',
};

// Turns a snake_case column name into a Title Case header.
function humanize(key) {
    if (HEADER_OVERRIDES[key]) return HEADER_OVERRIDES[key];
    return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
}

const isDateKey = (key) => /(date|_at|created)/i.test(key);

// Currency-ish columns (money), excluding date columns and count-style columns
// that happen to contain a money word (e.g. "spend_entries", "days_past_due").
const isCurrencyKey = (key) =>
    /(amount|spend|total|estimated|cost|fee|balance|revenue|payment|gross|sale|invoice)/i.test(key) &&
    !/(entries|count|days|date|_at|_id|\bid\b)/i.test(key);

// ROI columns, kept as the plain revenue/spend multiple: 1.00 means the spend
// came back exactly, 1.20 means a 20% return over it.
const isRoiKey = (key) => /(^|_)roi(_|$)/i.test(key);

// Rate columns, carried as a percent: 4.05 means 4.05%.
const isPercentKey = (key) => /(^|_)rate$/i.test(key);

// Elapsed-time columns, carried as a raw second count and shown as MM:SS.
const isDurationKey = (key) => /(^|_)duration(_|$)/i.test(key);

// Long free-text columns (call summaries, next steps). In a drill-down they get
// the spare width and show their full text on hover.
const isTextKey = (key) => /(summary|next_steps|notes|description|comment)/i.test(key);

// A Postgres date/timestamp comes back as an ISO string; show just the date.
// Columns a report pre-formats in SQL (e.g. a call time converted to Pacific)
// arrive as plain text and are shown exactly as the query built them.
function formatDate(value) {
    if (value == null || value === '') return '';
    const text = String(value);
    return /^\d{4}-\d{2}-\d{2}T/.test(text) ? text.split('T')[0] : text;
}

// Seconds -> MM:SS (MMM:SS past an hour, rather than silently wrapping).
function formatDuration(value) {
    if (value == null || value === '') return '';
    const seconds = Number(value);
    if (!Number.isFinite(seconds)) return '';
    const whole = Math.max(0, Math.round(seconds));
    const minutes = Math.floor(whole / 60);
    return `${String(minutes).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`;
}

// Renders a cell as a hyperlink using the report's URL template for that column
// ({{value}} -> the URL-encoded cell value). Opens in a new tab. Sorting,
// filtering, and export still use the underlying value.
function LinkCellRenderer(params) {
    const { value, template } = params;
    if (value === null || value === undefined || value === '') return '';
    // The pinned total row can put its "Total" label in a linked column —
    // render it as plain text, not a link to a nonsense URL.
    if (params.node && params.node.rowPinned) return String(value);
    const url = template.replace('{{value}}', encodeURIComponent(value));
    return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="report-cell-link">
            {value}
        </a>
    );
}

function makeColDef(key, links) {
    const def = { field: key, headerName: humanize(key) };
    // Most-specific checks first: "sale_date" is a date, not money, and
    // "gross_roi" is a ratio even though "gross" is a money word.
    if (isDateKey(key)) {
        def.valueFormatter = p => formatDate(p.value);
    } else if (isDurationKey(key)) {
        def.type = 'rightAligned';
        def.filter = 'agNumberColumnFilter';
        def.valueFormatter = p => formatDuration(p.value);
    } else if (isRoiKey(key)) {
        def.type = 'rightAligned';
        def.filter = 'agNumberColumnFilter';
        def.valueFormatter = p => (p.value == null || p.value === '' ? '' : Number(p.value).toFixed(2));
        def.context = { exportRaw: true };
    } else if (isPercentKey(key)) {
        def.type = 'rightAligned';
        def.filter = 'agNumberColumnFilter';
        def.valueFormatter = p => (p.value == null || p.value === '' ? '' : `${Number(p.value).toFixed(2)}%`);
        def.context = { exportRaw: true };
    } else if (isCurrencyKey(key)) {
        def.type = 'rightAligned';
        def.filter = 'agNumberColumnFilter';
        def.valueFormatter = p => (p.value == null || p.value === '' ? '' : currencyFormatter(Number(p.value), '$'));
        // Sum currency columns when the grid is grouped by another column.
        def.aggFunc = 'sum';
        // Export the underlying number, not the "$1,234.00" string, so the
        // CSV/Excel stays spreadsheet-friendly.
        def.context = { exportRaw: true };
    }
    // Render as a hyperlink when the report declares a URL template for it.
    if (links && links[key]) {
        def.cellRenderer = LinkCellRenderer;
        def.cellRendererParams = { template: links[key] };
    }
    return def;
}

// The union of keys present across the returned rows, in first-seen order.
// Only used for reports that don't declare `columns`.
function deriveKeys(rows) {
    const seen = new Set();
    const keys = [];
    for (const row of rows) {
        for (const k of Object.keys(row)) {
            if (!seen.has(k)) { seen.add(k); keys.push(k); }
        }
    }
    return keys;
}

// Builds the single pinned bottom row summing `totals` across every returned
// row, labelled "Total" in `leadColumn`. Returns undefined when the report
// declares no totals (or there's nothing to sum), which leaves the grid without
// a pinned row at all.
function buildTotalsRow(rows, totals, leadColumn) {
    if (!totals || !totals.length || !rows.length) return undefined;

    const row = {};
    for (const key of totals) {
        row[key] = rows.reduce((sum, r) => {
            const n = Number(r[key]);
            return Number.isFinite(n) ? sum + n : sum;
        }, 0);
    }

    // Label the row in the leading column, unless that column is itself being
    // summed (in which case its number stays).
    if (leadColumn && !totals.includes(leadColumn)) row[leadColumn] = 'Total';

    // ROI multiples can't be summed, so the total row derives each blended
    // ROI from the summed revenue over the summed spend basis, the hidden
    // column each report's SQL divides by.
    const ROI_TOTALS = {
        gross_roi: 'total_invoiced',
        ip_roi: 'total_initial',
        tp_roi: 'total_payment',
    };
    const basis = rows.reduce((sum, r) => {
        const n = Number(r.spend_basis);
        return Number.isFinite(n) ? sum + n : sum;
    }, 0);
    for (const [roiKey, revenueKey] of Object.entries(ROI_TOTALS)) {
        if (row[revenueKey] !== undefined && basis > 0) row[roiKey] = row[revenueKey] / basis;
    }

    // Conversion rates don't sum either: blended deals over blended cases.
    if (row.deals !== undefined && row.cases > 0) row.conversion_rate = (row.deals / row.cases) * 100;
    return [row];
}

// Local YYYY-MM-DD (avoids the UTC shift toISOString() would introduce).
function toISODate(date) {
    const tz = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tz).toISOString().split('T')[0];
}

function defaultRange() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { startDate: toISODate(start), endDate: toISODate(now) };
}

export default function ReportView() {
    const { key } = useParams();
    const { reports, loading: catalogLoading } = useReports();
    const catalogMeta = reports.find(r => r.key === key);

    const [range, setRange] = useState(defaultRange);
    const [rows, setRows] = useState([]);
    // The report key the current rows belong to. While switching reports, rows
    // lag one render behind `key`, so anything derived from them (the total
    // row) must not be computed against the NEW report's config.
    const [rowsFor, setRowsFor] = useState(null);
    // The run endpoint returns { report, rows } — the server's CURRENT metadata
    // for the report alongside its data.
    const [runMeta, setRunMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastRefreshed, setLastRefreshed] = useState(null);
    // The subset of rows currently surviving the grid's filters (quick filter +
    // column filters), reported by DataGrid — the total row sums these, so
    // filtering the view filters the totals. null = no report yet / fresh data.
    const [visibleRows, setVisibleRows] = useState(null);
    useEffect(() => { setVisibleRows(null); }, [rows]);

    // Prefer the metadata that arrived with the last run over the catalog's.
    // The catalog is fetched once per tab session, so a tab that outlives a
    // server deploy keeps stale metadata — columns/totals added on the server
    // silently missing until a hard refresh. The run response can't be stale:
    // it was generated by the same server call that produced the rows. (Guarded
    // by key so switching reports doesn't briefly apply the previous report's
    // metadata to the next one's rows.)
    const meta = runMeta && runMeta.key === key ? runMeta : catalogMeta;

    // Values for the report's declared toggles (e.g. attributing cases by
    // create date vs sale date). An unset key simply isn't sent — the server
    // falls back to the toggle's declared default — so the first run doesn't
    // have to wait for the metadata to arrive.
    const [toggles, setToggles] = useState({});
    useEffect(() => { setToggles({}); }, [key]);

    const load = useCallback(async (togglesOverride, rangeOverride) => {
        setLoading(true);
        setError(null);
        try {
            const params = meta?.date_range ? { ...(rangeOverride || range) } : {};
            Object.assign(params, togglesOverride || toggles);
            const { report, rows: data } = await runReport(key, params);
            setRunMeta(report || null);
            setRows(data || []);
            setRowsFor(key);
            setLastRefreshed(new Date());
        } catch (err) {
            setError(err.message);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [key, meta?.date_range, range, toggles]);

    // A toggle is an atomic choice, so clicking one re-runs the report
    // immediately.
    const handleToggle = useCallback((toggleKey, value) => {
        const next = { ...toggles, [toggleKey]: value };
        setToggles(next);
        load(next);
    }, [toggles, load]);

    // The picker only reports complete ranges, so a new range re-runs at once.
    const handleRange = useCallback((next) => {
        setRange(next);
        load(undefined, next);
    }, [load]);

    // Auto-run when the report changes. Date-range reports also re-run when the
    // range changes or the user hits "Run".
    useEffect(() => {
        // Wait until the catalog is loaded so we know whether to send dates.
        if (catalogLoading) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, catalogLoading]);

    // Column order follows the report's declared `columns` when present — rows
    // are JSON-encoded from Go maps, so their key order isn't meaningful.
    // Reports without it fall back to the keys the rows actually carry, reduced
    // to a string so re-running a report with the same shape doesn't hand the
    // grid a fresh array (which would reset column widths and sort/filter state
    // on every refresh).
    const derivedKeys = useMemo(
        () => (meta?.columns?.length ? '' : deriveKeys(rows).join(',')),
        [rows, meta?.columns],
    );

    const columnDefs = useMemo(() => {
        const keys = meta?.columns?.length
            ? meta.columns
            : (derivedKeys ? derivedKeys.split(',') : []);
        const totals = meta?.totals || [];
        // The pinned row writes the word "Total" into the leading column. AG Grid
        // infers a cell data type per column from the row data, and a string in a
        // column it inferred as numeric (an id, a case number) renders as
        // "Invalid Number" — so turn inference off for that one column.
        const labelKey = totals.length && keys.length && !totals.includes(keys[0]) ? keys[0] : null;
        return keys.map((key, i) => {
            const def = makeColDef(key, meta?.links);
            if (key === labelKey) def.cellDataType = false;
            // Explicitly null otherwise: the grid carries a column's pinning
            // over to a same-named column when switching reports.
            def.pinned = meta?.frozen?.includes(key) ? 'left' : null;
            // Reports with a drill-down get the expand chevron on the leading
            // column — except on the pinned total row, which has no detail.
            // When that column is also a link (allCases: case_number opens the
            // case in Logics), the link renderer has to go *inside* the group
            // renderer: a cellRendererSelector wins over cellRenderer outright,
            // so the chevron would otherwise replace the link.
            if (i === 0 && meta?.detail) {
                const template = meta?.links?.[key];
                const groupCell = {
                    component: 'agGroupCellRenderer',
                    params: template
                        ? { innerRenderer: LinkCellRenderer, innerRendererParams: { template } }
                        : undefined,
                };
                def.cellRendererSelector = (p) => (p.node.rowPinned ? undefined : groupCell);
            }
            return def;
        });
    }, [meta?.columns, meta?.frozen, meta?.links, meta?.totals, meta?.detail, derivedKeys]);

    const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, minWidth: 90 }), []);

    // Drill-down (master/detail) wiring, driven by the report's `detail`
    // metadata. The sub-rows arrive as a JSON string column on each row (the
    // generic row scanner doesn't parse JSON), so parse lazily and cache per
    // row object.
    const detailCache = useMemo(() => new WeakMap(), []);
    const parseDetail = useCallback((data) => {
        const field = meta?.detail?.rows_field;
        if (!field || !data) return [];
        if (detailCache.has(data)) return detailCache.get(data);
        let out = [];
        const raw = data[field];
        if (Array.isArray(raw)) {
            out = raw;
        } else if (typeof raw === 'string' && raw) {
            try { out = JSON.parse(raw) || []; } catch { out = []; }
        }
        detailCache.set(data, out);
        return out;
    }, [meta?.detail?.rows_field, detailCache]);

    // Total row for a sub-table: sums its currency columns, labelled in the
    // leading column. Recomputed from whatever subset a filter leaves visible.
    const buildDetailTotals = useCallback((subRows, columns) => {
        if (!subRows.length) return [];
        const moneyCols = columns.filter(c => !isDateKey(c) && isCurrencyKey(c));
        if (!moneyCols.length) return [];
        const row = {};
        for (const c of moneyCols) {
            row[c] = subRows.reduce((sum, r) => {
                const n = Number(r[c]);
                return Number.isFinite(n) ? sum + n : sum;
            }, 0);
        }
        row[columns[0]] = 'Total';
        return [row];
    }, []);

    const detailProps = useMemo(() => {
        if (!meta?.detail) return {};
        const columns = meta.detail.columns;
        const detailColumnDefs = columns.map((k, i) => {
            const def = makeColDef(k, meta?.links);
            // The leading column carries the total row's "Total" label; keep
            // AG Grid from type-checking the string against a numeric column.
            if (i === 0) def.cellDataType = false;
            // A call summary is a paragraph, not a field: give it the spare
            // width and the full text on hover, since the row is one line tall.
            if (isTextKey(k)) {
                def.flex = 3;
                def.minWidth = 220;
                def.tooltipField = k;
            }
            return def;
        });
        return {
            masterDetail: true,
            // Only rows that actually have sub-rows get an expander.
            isRowMaster: (data) => parseDetail(data).length > 0,
            // Size the expanded panel to its sub-rows — header, rows, status
            // bar, plus a row for the pinned total when this sub-table has one
            // (a call list has nothing to sum) — capped so a source with
            // hundreds of cases scrolls inside its own sub-table instead of
            // stretching the page.
            getRowHeight: (p) => {
                if (!p.node.detail) return undefined;
                const subRows = parseDetail(p.node.parent?.data);
                const totalRows = subRows.length + buildDetailTotals(subRows, columns).length;
                return Math.min(128 + totalRows * 42, 520);
            },
            // A function so each expanded row gets its own grid config, with
            // its own total row derived from its own cases.
            detailCellRendererParams: (masterParams) => {
                const subRows = parseDetail(masterParams.data);
                return {
                    detailGridOptions: {
                        columnDefs: detailColumnDefs,
                        defaultColDef: { sortable: true, filter: true, resizable: true, minWidth: 110, flex: 1 },
                        // Range-select cells to see sum/avg/min/max/count of the
                        // selection in the sub-table's own status bar.
                        cellSelection: true,
                        statusBar: {
                            statusPanels: [{ statusPanel: 'agAggregationComponent', align: 'right' }],
                        },
                        pinnedBottomRowData: buildDetailTotals(subRows, columns),
                        // Keep the sub-table's total in step with its own
                        // filters (same visible-rows rule as the main grid).
                        onModelUpdated: (e) => {
                            const visible = [];
                            e.api.forEachNodeAfterFilter((n) => { if (n.data) visible.push(n.data); });
                            applyPinnedTo(e.api, buildDetailTotals(visible, columns));
                        },
                    },
                    getDetailRowData: (p) => p.successCallback(subRows),
                };
            },
        };
    }, [meta?.detail, meta?.links, parseDetail, buildDetailTotals]);

    // Pinned total row for the columns the report declares in `totals`. It sums
    // every returned row (not just the filtered view), and reuses each column's
    // own formatter so the totals read as currency like the cells above them.
    const leadColumn = columnDefs.length ? columnDefs[0].field : null;
    const totalsSource = visibleRows ?? rows;
    const pinnedBottomRowData = useMemo(
        () => (rowsFor === key ? buildTotalsRow(totalsSource, meta?.totals, leadColumn) : undefined),
        [totalsSource, rowsFor, key, meta?.totals, leadColumn],
    );

    // Evaluated by DataGrid at export time, so it reflects the current dates.
    const exportFileName = useCallback(() => {
        const base = (meta?.label || key).replace(/[^\w]+/g, '_').replace(/^_|_$/g, '');
        return meta?.date_range
            ? `${base}_${range.startDate}_to_${range.endDate}`
            : `${base}_${toISODate(new Date())}`;
    }, [meta?.label, meta?.date_range, key, range.startDate, range.endDate]);

    if (!catalogLoading && !meta) {
        return (
            <div className="page">
                <h1 className="page__title">Report not found</h1>
                <p className="page__subtitle">No report is configured for “{key}”.</p>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="report__header">
                <div>
                    <h1 className="page__title">{meta?.label || 'Report'}</h1>
                    {meta?.description && <p className="page__subtitle">{meta.description}</p>}
                </div>
            </div>

            <div className="report__controls">
                {meta?.date_range && (
                    <div className="report__control">
                        <span>Dates</span>
                        <DateRangePicker value={range} onChange={handleRange} disabled={loading} />
                    </div>
                )}
                {(meta?.toggles || []).map(t => (
                    <div className="report__control" key={t.key}>
                        <span>{t.label}</span>
                        <div className="report__segmented">
                            {t.options.map(o => (
                                <button
                                    key={o.value}
                                    type="button"
                                    className={(toggles[t.key] ?? t.default) === o.value ? 'report__segmented--on' : ''}
                                    onClick={() => handleToggle(t.key, o.value)}
                                    disabled={loading}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
                <button className="blueButton" onClick={() => load()} disabled={loading}>
                    {loading ? 'Running…' : 'Run'}
                </button>
                {lastRefreshed && (
                    <span className="report__refreshed">
                        {rows.length} row{rows.length === 1 ? '' : 's'} · {lastRefreshed.toLocaleTimeString()}
                    </span>
                )}
            </div>

            {error && <p className="report__error">{error}</p>}

            {!error && !loading && rows.length === 0 && (
                <p className="page__subtitle">No rows returned for this report.</p>
            )}

            {rows.length > 0 && (
                <DataGrid
                    rowData={rows}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    pinnedBottomRowData={pinnedBottomRowData}
                    height="calc(100vh - 260px)"
                    enableExport
                    exportFileName={exportFileName}
                    onVisibleRowsChanged={setVisibleRows}
                    {...detailProps}
                />
            )}
        </div>
    );
}
